import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ShieldCheck, Award, Download } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG =
  "https://images.unsplash.com/photo-1496247749665-49cf5b1022e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHw0fHxpbmR1c3RyaWFsJTIwZmFjdG9yeSUyMG1hbnVmYWN0dXJpbmclMjBtYWNoaW5lcnl8ZW58MHx8fHwxNzkwMjM1OTg5fDA&ixlib=rb-4.1.0&q=85&w=1920&auto=format";

const LINES = [
  { text: "THIRTY YEARS", cls: "text-white" },
  { text: "OF BUILDING", cls: "text-stroke" },
  { text: "TRUST.", cls: "text-brand-red" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="home" ref={ref} className="relative flex min-h-screen items-center overflow-hidden">
      {/* Parallax background */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Techworld Business Group manufacturing facility"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/70 to-ink" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/40 to-transparent" />
      <div className="red-glow absolute -left-40 top-1/3 h-[560px] w-[560px]" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-24 pt-36 sm:px-6 lg:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mb-6 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-slate-300 sm:text-xs"
        >
          <span className="h-px w-10 bg-brand-red" />
          Techworld Business Group • Alappuzha, Kerala
        </motion.p>

        <h1 className="font-display text-[clamp(2.9rem,9vw,7.6rem)] font-semibold leading-[0.95] tracking-tight">
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.cls}`}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.2 + i * 0.15, ease: [0.16, 1, 0.3, 1] }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.75 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
        >
          An ISO 9001:2015 certified business group focused on importing, exporting
          and manufacturing — with a dedicated team working diligently all over Kerala.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.88 }}
          className="mt-6 flex flex-wrap items-center gap-3"
        >
          {["Manufacturing", "Distribution", "Online", "Institute of Management Studies"].map((t) => (
            <span
              key={t}
              className="rounded-full border border-white/15 bg-white/5 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-200 backdrop-blur-sm sm:text-[11px]"
            >
              {t}
            </span>
          ))}
          <span className="flex items-center gap-2 rounded-full border border-brand-red/40 bg-brand-red/10 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-brand-bright sm:text-[11px]">
            <ShieldCheck size={13} /> ISO 9001:2015
          </span>
          <span className="flex items-center gap-2 rounded-full border border-brand-red/40 bg-brand-red/10 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-brand-bright sm:text-[11px]">
            <Award size={13} /> FSSAI Certified
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-explore-button"
            onClick={() => scrollToId("operations")}
            className="group relative overflow-hidden rounded-full bg-brand-red px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.22em] text-white transition-all duration-300 hover:shadow-[0_0_36px_rgba(220,38,38,0.5)]"
          >
            <span className="relative z-10">Explore Our Business</span>
            <span className="absolute inset-0 -translate-x-full bg-brand-bright transition-transform duration-500 group-hover:translate-x-0" />
          </button>
          <button
            data-testid="hero-contact-button"
            onClick={() => scrollToId("contact")}
            className="rounded-full border border-white/20 px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-white transition-all duration-300 hover:border-brand-red hover:text-brand-bright"
          >
            Contact Us
          </button>
          <a
            data-testid="hero-catalogue-button"
            href={`${process.env.PUBLIC_URL}/techworld-catalogue.pdf`}
            download="Techworld-Group-Catalogue.pdf"
            className="flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-white transition-all duration-300 hover:border-brand-red hover:text-brand-bright"
          >
            <Download size={15} />
            Download Catalogue
          </a>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => scrollToId("about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        style={{ opacity: fade }}
        data-testid="hero-scroll-cue"
        className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2 text-slate-400"
        aria-label="Scroll down"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
